import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/** Mirrors server `SessionUser` from `/api/auth/me` (Passport session). */
export type SessionUser = {
  id?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  provider: 'google' | 'microsoft';
  /** Stripe Price id when the user has an active purchased plan. */
  pricePlan?: string | null;
  /** Friendly plan / product name. */
  pricePlanName?: string | null;
  /** ISO timestamp of when `pricePlan` was purchased. */
  pricePlanAt?: string | null;
  /** Stripe Customer id (`cus_…`). */
  stripeCustomerId?: string | null;
  /** Stripe Subscription id (`sub_…`). */
  stripeSubscriptionId?: string | null;
  /** School membership: owner, approved staff, or none. */
  schoolRole?: 'owner' | 'staff' | 'none';
  /** Latest staff access request status when not an owner. */
  schoolAccessStatus?: 'pending' | 'approved' | 'rejected' | 'revoked' | null;
  /** Public school id for owned or requested school, when known. */
  schoolPublicId?: string | null;
};

type MeResponse = { user: SessionUser | null };

type UserStatus = 'loading' | 'ready';

type UserContextValue = {
  user: SessionUser | null;
  status: UserStatus;
  refreshUser: () => Promise<void>;
};

const UserContext = createContext<UserContextValue | undefined>(undefined);

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [status, setStatus] = useState<UserStatus>('loading');

  const refreshUser = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', {
        credentials: 'include',
      });
      if (!res.ok) {
        setUser(null);
        return;
      }
      const data = (await res.json()) as MeResponse;
      setUser(data.user ?? null);
    } catch {
      setUser(null);
    } finally {
      setStatus('ready');
    }
  }, []);

  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  const value = useMemo(
    () => ({
      user,
      status,
      refreshUser,
    }),
    [user, status, refreshUser],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContextValue => {
  const ctx = useContext(UserContext);
  if (ctx === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return ctx;
};
